/**
 * SVG Chart components for company data visualization
 */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { safeParseNumber } from '../utils/numberUtils';
import { jobChartVar, jobChartSeriesVars } from '../utils/job-detail-chart-theme';
import { getHiringTrendReason } from '../utils/hiringTrendAnalysis';
import { ChartPopoverPortal } from './ChartPopoverPortal';
import { usePrefersFineHover } from '../utils/usePrefersFineHover';
import { JD_MORE_INFO_CHART_HIT } from '../../../../../utils/job-detail/job-detail-more-info-affordance';
import { toSafeIntlStringValue } from '../../../../../utils/intl/to-safe-intl-string-value';

/**
 * Calculate the relative luminance of a color (0-1, where 0 is black and 1 is white)
 * Based on WCAG 2.0 formula
 */
function getLuminance(hex: string): number {
  try {
    // Remove # if present
    const cleanHex = hex.replace('#', '').toLowerCase();
    
    // Handle 3-digit hex colors
    const fullHex = cleanHex.length === 3
      ? cleanHex.split('').map(char => char + char).join('')
      : cleanHex;
    
    // Parse RGB values
    const r = parseInt(fullHex.substring(0, 2), 16) / 255;
    const g = parseInt(fullHex.substring(2, 4), 16) / 255;
    const b = parseInt(fullHex.substring(4, 6), 16) / 255;
    
    // Apply gamma correction
    const [rLinear, gLinear, bLinear] = [r, g, b].map(val => {
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    
    // Calculate relative luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  } catch {
    // Fallback: assume dark color if parsing fails
    return 0.2;
  }
}

/**
 * Determine if a color is light or dark, and return appropriate text color
 */
function getContrastingTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  return luminance < 0.5 ? '#ffffff' : '#1E1A14'; // white or jc ink
}

function labelFillForBarFill(fill: string): string {
  if (fill.startsWith('var(')) return '#ffffff';
  return getContrastingTextColor(fill);
}

interface MoneyGraphProps {
  revenue?: string | number;
  profit?: string | number;
  profitMargin?: string | number;
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
  /** Tighter layout for employer profile and dense dashboards */
  density?: 'default' | 'compact';
}

/**
 * Money graph showing revenue, profit, and profit margin (2024 data)
 */
export const MoneyGraph: React.FC<MoneyGraphProps> = memo(({
  revenue,
  profit,
  profitMargin,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
  density = 'default',
}) => {
  const intl = useIntl();
  const isCompact = density === 'compact';
  const safeCompanyName = toSafeIntlStringValue(companyName, locale);
  const primaryColor =
    brandColour && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(brandColour.trim())
      ? brandColour.trim()
      : jobChartVar.cta;

  const revenueNum = safeParseNumber(revenue);
  const profitNum = safeParseNumber(profit);
  const profitMarginNum = safeParseNumber(profitMargin);

  // Only render if at least one valid number exists (including 0)
  // Check if any of the parsed values is not null (null means invalid, 0 is valid)
  const hasValidData = revenueNum !== null || profitNum !== null || profitMarginNum !== null;
  if (!hasValidData) {
    return null;
  }

  // Count how many metrics we have to display
  const metricsCount = [
    revenueNum !== null && revenueNum !== 0,
    profitNum !== null && profitNum !== 0,
    profitMarginNum !== null && profitMarginNum !== 0
  ].filter(Boolean).length;

  // Calculate max value for scaling
  const maxValue = Math.max(
    revenueNum !== null ? Math.abs(revenueNum) : 0,
    profitNum !== null ? Math.abs(profitNum) : 0,
    1
  );

  const width = isCompact ? 260 : 320;
  const barHeight = isCompact ? 17 : 22;
  const barSpacing = isCompact ? 26 : 34;
  const padding = isCompact ? 10 : 14;
  const labelWidth = isCompact ? 54 : 68;
  const chartAreaWidth = width - padding * 2 - labelWidth;
  const barAreaStartX = padding + labelWidth;
  const labelFont = isCompact ? 9 : 11;
  const valueFont = isCompact ? 9 : 11;
  const valueFontOutside = isCompact ? 8 : 10;

  const chartHeight = metricsCount > 0 ? (metricsCount * barSpacing) + padding : isCompact ? 56 : 72;
  const height = chartHeight + (isCompact ? 16 : 22);
  
  let currentY = padding + (isCompact ? 10 : 14);
  const bars: Array<{
    y: number;
    value: number;
    label: string;
    color: string;
    isMargin: boolean;
  }> = [];

  if (revenueNum !== null && revenueNum !== 0) {
    bars.push({
      y: currentY,
      value: revenueNum,
      label: intl.formatMessage({ id: 'companyChart.revenue', defaultMessage: 'Revenue', description: 'Label for revenue bar in financial overview chart' }),
      color: primaryColor,
      isMargin: false
    });
    currentY += barSpacing;
  }

  if (profitNum !== null && profitNum !== 0) {
    bars.push({
      y: currentY,
      value: profitNum,
      label: intl.formatMessage({ id: 'companyChart.profit', defaultMessage: 'Profit', description: 'Label for profit bar in financial overview chart' }),
      color: profitNum >= 0 ? jobChartVar.accentSoft : jobChartVar.filterCompanyBorder,
      isMargin: false
    });
    currentY += barSpacing;
  }

  if (profitMarginNum !== null && profitMarginNum !== 0) {
    bars.push({
      y: currentY,
      value: profitMarginNum,
      label: intl.formatMessage({ id: 'companyChart.profitMargin', defaultMessage: 'Profit Margin', description: 'Label for profit margin bar in financial overview chart (shown as percentage)' }),
      color: profitMarginNum >= 0 ? jobChartVar.gold : jobChartVar.filterCompanyBorder,
      isMargin: true
    });
  }

  const formatValue = (value: number, isMargin = false) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    if (isMargin) {
      return `${sign}${absValue.toFixed(1)}%`;
    }
    if (absValue >= 1000000) {
      return `${sign}€${(absValue / 1000000).toFixed(1)}M`;
    }
    if (absValue >= 1000) {
      return `${sign}€${(absValue / 1000).toFixed(1)}K`;
    }
    return `${sign}€${absValue.toFixed(0)}`;
  };

  // Generate FAQ-style caption with i18n
  const generateCaption = () => {
    if (!safeCompanyName) return null;
    
    const parts: React.ReactNode[] = [];
    
    if (profitNum !== null && profitNum < 0) {
      parts.push(
        <FormattedMessage
          key="unprofitable"
          id="companyChart.caption.unprofitable"
          defaultMessage="{companyName} was unprofitable in 2024"
          description="Caption text indicating a company had negative profit in 2024, shown in financial overview chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    } else if (profitNum !== null && profitNum > 0) {
      parts.push(
        <FormattedMessage
          key="profitable"
          id="companyChart.caption.profitable"
          defaultMessage="{companyName} was profitable in 2024"
          description="Caption text indicating a company had positive profit in 2024, shown in financial overview chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    }
    
    if (revenueNum !== null && revenueNum > 0) {
      const revenueText = formatValue(revenueNum);
      if (parts.length === 0) {
        parts.push(
          <FormattedMessage
            key="revenue"
            id="companyChart.caption.revenue"
            defaultMessage="{companyName} had revenue of {revenue} in 2024"
            description="Caption text showing company revenue for 2024 in financial overview chart, where {revenue} is the formatted revenue amount"
            values={{ companyName: safeCompanyName, revenue: revenueText }}
          />
        );
      } else {
        parts.push(
          <FormattedMessage
            key="revenue-with"
            id="companyChart.caption.revenueWith"
            defaultMessage="with revenue of {revenue}"
            description="Caption text fragment appended to other financial information, showing revenue amount"
            values={{ revenue: revenueText }}
          />
        );
      }
    }
    
    if (profitMarginNum !== null && profitMarginNum !== 0) {
      const marginText = formatValue(profitMarginNum, true);
      if (parts.length > 0) {
        parts.push(
          <FormattedMessage
            key="margin"
            id="companyChart.caption.margin"
            defaultMessage="and a profit margin of {margin}"
            description="Caption text fragment appended to revenue information, showing profit margin percentage"
            values={{ margin: marginText }}
          />
        );
      } else {
        parts.push(
          <FormattedMessage
            key="margin-only"
            id="companyChart.caption.marginOnly"
            defaultMessage="{companyName} had a profit margin of {margin} in 2024"
            description="Caption text shown when only profit margin data is available (no revenue), displayed in financial overview chart"
            values={{ companyName: safeCompanyName, margin: marginText }}
          />
        );
      }
    }
    
    return parts.length > 0 ? (
      <p className={`${isCompact ? 'text-[10px]' : 'text-xs'} text-jc-muted mb-1 leading-snug`}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {part}
            {idx < parts.length - 1 ? ' ' : '.'}
          </React.Fragment>
        ))}
      </p>
    ) : null;
  };

  const caption = generateCaption();

  return (
    <div className={`money-graph ${className} flex flex-col items-center`}>
      <h3 className={`${isCompact ? 'text-[10px]' : 'text-xs'} font-semibold text-jc-ink mb-1 w-full`}>
        <FormattedMessage
          id="companyChart.financialOverview"
          defaultMessage="Financial Overview (2024)"
          description="Section heading for company financial data chart showing revenue, profit, and profit margin for 2024"
        />
      </h3>
      {caption && <div className="w-full">{caption}</div>}
      <svg
        width={width}
        height={height}
        className="w-full max-w-full mx-auto overflow-visible"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Render bars */}
        {bars.map((bar, index) => {
          // For margin, use percentage scale (0-100%), otherwise use value scale
          const barWidth = bar.isMargin
            ? (Math.abs(bar.value) / 100) * chartAreaWidth
            : (Math.abs(bar.value) / maxValue) * chartAreaWidth;
          
          const barX = barAreaStartX;
          const valueText = formatValue(bar.value, bar.isMargin);
          
          return (
            <g key={index}>
              {/* Background bar */}
              <rect
                x={barX}
                y={bar.y}
                width={chartAreaWidth}
                height={barHeight}
                fill={jobChartVar.elevated}
                rx="3"
              />
              {/* Value bar */}
              <rect
                x={barX}
                y={bar.y}
                width={barWidth}
                height={barHeight}
                fill={bar.color}
                rx="3"
              />
              {/* Value text inside bar (or outside if bar is too short) */}
              {barWidth > 16 ? (
                <text
                  x={barX + barWidth / 2}
                  y={bar.y + barHeight / 2}
                  dominantBaseline="middle"
                  textAnchor="middle"
                  fill={labelFillForBarFill(bar.color)}
                  fontSize={valueFont}
                  fontWeight={600}
                >
                  {valueText}
                </text>
              ) : (
                <text
                  x={barX + barWidth + 6}
                  y={bar.y + barHeight / 2}
                  dominantBaseline="middle"
                  textAnchor="start"
                  fill="var(--jc-ink-secondary)"
                  fontSize={valueFontOutside}
                  fontWeight={600}
                >
                  {valueText}
                </text>
              )}
              <text
                x={barX - 8}
                y={bar.y + barHeight / 2}
                dominantBaseline="middle"
                textAnchor="end"
                fill="var(--jc-muted)"
                fontSize={labelFont}
              >
                {bar.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
});

MoneyGraph.displayName = 'MoneyGraph';

interface MonthlyHiringChartProps {
  monthlyCounts?: Record<string, number>;
  headcountMin?: number;
  headcountMax?: number;
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
  density?: 'default' | 'compact';
}

/**
 * Monthly hiring bar chart with trend analysis
 * Enhanced analysis considers company size when headcount range is available
 */
export const MonthlyHiringChart: React.FC<MonthlyHiringChartProps> = memo(({
  monthlyCounts,
  headcountMin,
  headcountMax,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
  density = 'default',
}) => {
  const intl = useIntl();
  const isCompact = density === 'compact';
  const safeCompanyName = toSafeIntlStringValue(companyName, locale);
  const [hiringPopover, setHiringPopover] = useState<{ monthKey: string; value: number; anchor: Element } | null>(null);
  const prefersFineHoverHiring = usePrefersFineHover();
  const hiringHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHiringHover = useCallback(() => {
    if (hiringHoverTimer.current !== null) {
      clearTimeout(hiringHoverTimer.current);
      hiringHoverTimer.current = null;
    }
  }, []);

  const scheduleHiringClose = useCallback(() => {
    clearHiringHover();
    hiringHoverTimer.current = setTimeout(() => {
      setHiringPopover(null);
      hiringHoverTimer.current = null;
    }, 220);
  }, [clearHiringHover]);

  useEffect(() => () => clearHiringHover(), [clearHiringHover]);

  const openHiringBar = useCallback(
    (monthKey: string, value: number, anchor: Element) => {
      clearHiringHover();
      setHiringPopover({ monthKey, value, anchor });
    },
    [clearHiringHover],
  );

  const closeHiringPopover = useCallback(() => {
    clearHiringHover();
    setHiringPopover(null);
  }, [clearHiringHover]);

  const toggleHiringBar = useCallback((monthKey: string, value: number, anchor: Element) => {
    setHiringPopover((prev) =>
      prev?.monthKey === monthKey ? null : { monthKey, value, anchor },
    );
  }, []);

  const primaryColor =
    brandColour && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(brandColour.trim())
      ? brandColour.trim()
      : jobChartVar.cta;

  if (!monthlyCounts || Object.keys(monthlyCounts).length === 0) {
    return null;
  }

  const validMonthlyCounts: Record<string, number> = {};
  Object.entries(monthlyCounts).forEach(([month, count]) => {
    const parsed = safeParseNumber(count);
    if (parsed !== null && parsed >= 0) {
      validMonthlyCounts[month] = parsed;
    }
  });

  if (Object.keys(validMonthlyCounts).length === 0) {
    return null;
  }

  // Sort months chronologically (YYYY-MM format)
  const sortedMonths = Object.keys(validMonthlyCounts).sort((a, b) => {
    // Parse YYYY-MM format
    const [yearA, monthA] = a.split('-').map(Number);
    const [yearB, monthB] = b.split('-').map(Number);
    
    // Compare by year first, then month
    if (yearA !== yearB) {
      return yearA - yearB;
    }
    return monthA - monthB;
  });
  
  const values = sortedMonths.map(month => validMonthlyCounts[month]);
  
  // Determine if we have enough data for meaningful trend analysis
  // Need at least 3 months to draw conclusions
  const hasEnoughDataForTrends = sortedMonths.length >= 3;

  // Calculate trends (only if we have enough data)
  const recentMonths = sortedMonths.slice(-3);
  const earlierMonths = sortedMonths.slice(0, Math.max(1, Math.floor(sortedMonths.length / 2)));
  
  const recentAvg = recentMonths.length > 0
    ? recentMonths.reduce((sum, month) => sum + validMonthlyCounts[month], 0) / recentMonths.length
    : 0;
  
  const earlierAvg = earlierMonths.length > 0 && hasEnoughDataForTrends
    ? earlierMonths.reduce((sum, month) => sum + validMonthlyCounts[month], 0) / earlierMonths.length
    : 0;

  // Enhanced trend analysis with headcount context (only if we have enough data)
  // Conservative thresholds: default to Stable unless change is clearly significant.
  let trend: 'rapidly-growing' | 'growing' | 'stable' | 'declining' | 'rapidly-declining' = 'stable';
  let trendLabel = 'Stable';
  let trendDescription = '';
  let avgHeadcount: number | null = null;
  let avgHiringText = '';

  const headcountMinNum = safeParseNumber(headcountMin);
  const headcountMaxNum = safeParseNumber(headcountMax);
  
  if (headcountMinNum !== null && headcountMaxNum !== null) {
    avgHeadcount = (headcountMinNum + headcountMaxNum) / 2;
  } else if (headcountMinNum !== null) {
    avgHeadcount = headcountMinNum;
  } else if (headcountMaxNum !== null) {
    avgHeadcount = headcountMaxNum;
  }

  // Only perform trend analysis if we have enough data
  if (hasEnoughDataForTrends && avgHeadcount !== null && avgHeadcount > 0) {
    const recentHiringRate = (recentAvg / avgHeadcount) * 100;
    const earlierHiringRate = (earlierAvg / avgHeadcount) * 100;
    
    if (recentAvg > earlierAvg * 1.4 && recentHiringRate > 7) {
      trend = 'rapidly-growing';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.rapidlyGrowing', defaultMessage: 'Rapidly Growing', description: 'Label for rapidly increasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.rapidlyGrowing', defaultMessage: 'Hiring {avg} jobs/month ({rate}% of workforce)', description: 'Description text for rapidly growing hiring trend showing average monthly jobs and percentage of workforce' },
        { avg: recentAvg.toFixed(1), rate: recentHiringRate.toFixed(1) }
      );
    } else if (recentAvg > earlierAvg * 1.25) {
      trend = 'growing';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.growing', defaultMessage: 'Growing', description: 'Label for increasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.growing', defaultMessage: 'Hiring {avg} jobs/month ({rate}% of workforce)', description: 'Description text for growing hiring trend showing average monthly jobs and percentage of workforce' },
        { avg: recentAvg.toFixed(1), rate: recentHiringRate.toFixed(1) }
      );
    } else if (recentAvg < earlierAvg * 0.55 && recentHiringRate < 0.8 && earlierAvg > 0) {
      // Rapidly declining: recent average is less than 70% of earlier average AND hiring rate is very low (<1% of workforce)
      // This indicates a significant drop in hiring activity
      trend = 'rapidly-declining';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.rapidlyDeclining', defaultMessage: 'Rapidly Declining', description: 'Label for rapidly decreasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.rapidlyDeclining', defaultMessage: 'Hiring {avg} jobs/month ({rate}% of workforce)', description: 'Description text for rapidly declining hiring trend showing average monthly jobs and percentage of workforce' },
        { avg: recentAvg.toFixed(1), rate: recentHiringRate.toFixed(1) }
      );
    } else if (recentAvg < earlierAvg * 0.75) {
      trend = 'declining';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.declining', defaultMessage: 'Declining', description: 'Label for decreasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.declining', defaultMessage: 'Hiring {avg} jobs/month ({rate}% of workforce)', description: 'Description text for declining hiring trend showing average monthly jobs and percentage of workforce' },
        { avg: recentAvg.toFixed(1), rate: recentHiringRate.toFixed(1) }
      );
    } else {
      trend = 'stable';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.stable', defaultMessage: 'Stable', description: 'Label for stable hiring trend (no significant change) in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.stable', defaultMessage: 'Hiring {avg} jobs/month ({rate}% of workforce)', description: 'Description text for stable hiring trend showing average monthly jobs and percentage of workforce' },
        { avg: recentAvg.toFixed(1), rate: recentHiringRate.toFixed(1) }
      );
    }
  } else if (hasEnoughDataForTrends) {
    // Fallback to simple analysis without headcount (only if we have enough data)
    if (recentAvg > earlierAvg * 1.25) {
      trend = 'growing';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.growing', defaultMessage: 'Growing', description: 'Label for increasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.simpleGrowing', defaultMessage: 'Hiring {avg} jobs/month on average', description: 'Simplified description text for growing hiring trend showing only average monthly jobs (when workforce data unavailable)' },
        { avg: recentAvg.toFixed(1) }
      );
    } else if (recentAvg < earlierAvg * 0.75) {
      trend = 'declining';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.declining', defaultMessage: 'Declining', description: 'Label for decreasing hiring trend in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.simpleDeclining', defaultMessage: 'Hiring {avg} jobs/month on average', description: 'Simplified description text for declining hiring trend showing only average monthly jobs (when workforce data unavailable)' },
        { avg: recentAvg.toFixed(1) }
      );
    } else {
      trend = 'stable';
      trendLabel = intl.formatMessage({ id: 'companyChart.trend.stable', defaultMessage: 'Stable', description: 'Label for stable hiring trend (no significant change) in monthly hiring chart' });
      trendDescription = intl.formatMessage(
        { id: 'companyChart.trendDescription.simpleStable', defaultMessage: 'Hiring {avg} jobs/month on average', description: 'Simplified description text for stable hiring trend showing only average monthly jobs (when workforce data unavailable)' },
        { avg: recentAvg.toFixed(1) }
      );
    }
  } else {
    // Not enough data for trend analysis - show "Limited Data" or similar
    trend = 'stable';
    trendLabel = intl.formatMessage({ id: 'companyChart.trend.insufficientData', defaultMessage: 'Limited Data', description: 'Label shown when there is not enough data to determine hiring trend' });
  }

  const maxValue = Math.max(...values, 1);
  const width = isCompact ? 300 : 360;
  const height = isCompact ? 124 : 158;
  const padding = isCompact ? 20 : 28;
  const chartHeight = height - padding * 2;
  const chartWidth = width - padding * 2;
  const barGap = isCompact ? 2 : 4;
  const slotW = chartWidth / sortedMonths.length;
  const barWidth = Math.max(2, slotW - barGap);
  const axisFont = isCompact ? 10 : 12;
  const barTopFont = isCompact ? 10 : 12;
  const minBarHeightForTopLabel = isCompact ? 13 : 16;

  const trendColors: Record<
    'rapidly-growing' | 'growing' | 'stable' | 'declining' | 'rapidly-declining',
    string
  > = {
    'rapidly-growing': jobChartVar.accent,
    growing: jobChartVar.accentSoft,
    stable: jobChartVar.muted,
    declining: jobChartVar.goldLine,
    'rapidly-declining': jobChartVar.filterCompanyBorder,
  };

  // Generate FAQ-style caption (only if we have enough data for meaningful conclusions)
  const generateCaption = () => {
    if (!safeCompanyName || !hasEnoughDataForTrends) return null;
    
    const parts: React.ReactNode[] = [];
    
    if (trend === 'rapidly-growing') {
      parts.push(
        <FormattedMessage
          key="rapidly-growing"
          id="companyChart.caption.hiringRapidlyGrowing"
          defaultMessage="{companyName}'s hiring is rapidly growing"
          description="Caption text describing a company with rapidly increasing hiring trend, shown in monthly hiring trends chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    } else if (trend === 'growing') {
      parts.push(
        <FormattedMessage
          key="growing"
          id="companyChart.caption.hiringGrowing"
          defaultMessage="{companyName}'s hiring is growing"
          description="Caption text describing a company with increasing hiring trend, shown in monthly hiring trends chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    } else if (trend === 'declining') {
      parts.push(
        <FormattedMessage
          key="declining"
          id="companyChart.caption.hiringDeclining"
          defaultMessage="{companyName}'s hiring is declining"
          description="Caption text describing a company with decreasing hiring trend, shown in monthly hiring trends chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    } else if (trend === 'rapidly-declining') {
      parts.push(
        <FormattedMessage
          key="rapidly-declining"
          id="companyChart.caption.hiringRapidlyDeclining"
          defaultMessage="{companyName}'s hiring is rapidly declining"
          description="Caption text describing a company with rapidly decreasing hiring trend, shown in monthly hiring trends chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    } else {
      parts.push(
        <FormattedMessage
          key="stable"
          id="companyChart.caption.hiringStable"
          defaultMessage="{companyName}'s hiring is stable"
          description="Caption text describing a company with stable hiring trend (no significant change), shown in monthly hiring trends chart"
          values={{ companyName: safeCompanyName }}
        />
      );
    }
    
    // Use actual calculated values instead of parsing the localized string
    if (recentAvg > 0) {
      parts.push(
        <FormattedMessage
          key="avg"
          id="companyChart.caption.averaging"
          defaultMessage="averaging {avg} jobs per month"
          description="Caption text fragment showing average monthly job postings, appended to hiring trend description"
          values={{ avg: recentAvg.toFixed(1) }}
        />
      );
      
      // Show workforce percentage if headcount is available
      if (avgHeadcount !== null && avgHeadcount > 0) {
        const hiringRate = (recentAvg / avgHeadcount) * 100;
        parts.push(
          <FormattedMessage
            key="rate"
            id="companyChart.caption.workforceRate"
            defaultMessage="({rate}% of their workforce)"
            description="Caption text fragment in parentheses showing hiring rate as percentage of total workforce, appended to hiring statistics"
            values={{ rate: hiringRate.toFixed(1) }}
          />
        );
      }
    }
    
    return parts.length > 0 ? (
      <p className="text-sm text-jc-muted mb-1 leading-snug">
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {part}
            {idx < parts.length - 1 ? ', ' : '.'}
          </React.Fragment>
        ))}
      </p>
    ) : null;
  };

  const caption = generateCaption();
  const trendReason = getHiringTrendReason({
    trend,
    hasEnoughDataForTrends,
    recentAvg,
    earlierAvg,
    avgHeadcount,
  });

  const formatMonthPeriod = (monthStr: string): string => {
    try {
      const [year, monthNum] = monthStr.split('-');
      const monthIndex = parseInt(monthNum, 10) - 1;
      const date = new Date(parseInt(year, 10), monthIndex, 1);
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  /** Linear regression trendline — computed outside JSX to avoid fragile IIFE patterns in the render tree. */
  let hiringTrendLine: React.ReactNode = null;
  if (hasEnoughDataForTrends && sortedMonths.length >= 2 && maxValue > 0) {
    const n = sortedMonths.length;
    const sumX = sortedMonths.reduce((sum, _, i) => sum + i, 0);
    const sumY = values.reduce((sum, val) => sum + val, 0);
    const sumXY = sortedMonths.reduce((sum, _, i) => sum + i * values[i], 0);
    const sumXX = sortedMonths.reduce((sum, _, i) => sum + i * i, 0);
    const denom = n * sumXX - sumX * sumX;
    if (denom !== 0 && Number.isFinite(denom)) {
      const slope = (n * sumXY - sumX * sumY) / denom;
      const intercept = (sumY - slope * sumX) / n;
      if (Number.isFinite(slope) && Number.isFinite(intercept)) {
        // Bar i is centered at padding + i * slotW + slotW / 2 (see bar loop below)
        const startX = padding + slotW / 2;
        const endX = padding + (n - 1) * slotW + slotW / 2;
        const startY = padding + chartHeight - (intercept / maxValue) * chartHeight;
        const endY = padding + chartHeight - (((slope * (n - 1) + intercept) / maxValue) * chartHeight);
        hiringTrendLine = (
          <line
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={trendColors[trend]}
            strokeWidth="1.5"
            strokeDasharray="4,2"
            opacity="0.85"
          />
        );
      }
    }
  }

  return (
    <div className={`monthly-hiring-chart ${className} flex flex-col items-center`}>
      <p className={`${isCompact ? 'text-[11px] mb-0.5' : 'text-xs mb-1'} text-jc-muted w-full text-center`}>
        <FormattedMessage
          id="jobChart.segmentHint"
          defaultMessage="Tap or hover a segment for details."
          description="Hint for interactive donut and pill chart"
        />
      </p>
      <div className={`${isCompact ? 'mb-1' : 'mb-2'} w-full`}>
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <h3 className={`${isCompact ? 'text-xs' : 'text-sm'} font-semibold text-jc-ink min-w-0`}>
            <FormattedMessage
              id="companyChart.monthlyHiringTrends"
              defaultMessage="Monthly Hiring Trends"
              description="Section heading for chart showing company hiring activity over time, displayed as monthly job posting counts"
            />
          </h3>
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: trendColors[trend] }} />
            <span className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium text-jc-ink-secondary`}>{trendLabel}</span>
          </div>
        </div>
        {caption && <div className="w-full">{caption}</div>}
        {trendDescription && !caption && hasEnoughDataForTrends && (
          <p className={`${isCompact ? 'text-[11px]' : 'text-sm'} text-jc-muted`}>{trendDescription}</p>
        )}
        {hasEnoughDataForTrends && trendReason.code !== 'insufficient-data' && (
          <p className={`${isCompact ? 'text-[11px] mt-0.5' : 'text-sm mt-1'} text-jc-muted leading-relaxed`}>
            {trendReason.code === 'no-baseline' ? (
              <FormattedMessage
                id="companyChart.trendReason.noBaseline"
                defaultMessage="Why this trend: there was little or no earlier hiring baseline, so we classify this using the latest months only."
                description="Explanation shown for hiring trend when earlier period average is zero or unavailable"
              />
            ) : trendReason.code === 'rapidly-growing' ? (
              <FormattedMessage
                id="companyChart.trendReason.rapidlyGrowing"
                defaultMessage="Why this trend: the latest 3-month average ({recentAvg}) is {change}% higher than the earlier baseline ({earlierAvg}) and hiring intensity is high at {rate}% of estimated headcount."
                description="Explanation shown for rapidly growing hiring trend with baseline comparison and hiring intensity"
                values={{
                  recentAvg: recentAvg.toFixed(1),
                  earlierAvg: earlierAvg.toFixed(1),
                  change: (trendReason.changePercent ?? 0).toFixed(1),
                  rate: (trendReason.recentHiringRate ?? 0).toFixed(1),
                }}
              />
            ) : trendReason.code === 'growing' ? (
              <FormattedMessage
                id="companyChart.trendReason.growing"
                defaultMessage="Why this trend: the latest 3-month average ({recentAvg}) is {change}% above the earlier baseline ({earlierAvg})."
                description="Explanation shown for growing hiring trend with baseline comparison"
                values={{
                  recentAvg: recentAvg.toFixed(1),
                  earlierAvg: earlierAvg.toFixed(1),
                  change: (trendReason.changePercent ?? 0).toFixed(1),
                }}
              />
            ) : trendReason.code === 'rapidly-declining' ? (
              <FormattedMessage
                id="companyChart.trendReason.rapidlyDeclining"
                defaultMessage="Why this trend: the latest 3-month average ({recentAvg}) is {change}% below the earlier baseline ({earlierAvg}) and hiring intensity is very low at {rate}% of estimated headcount."
                description="Explanation shown for rapidly declining hiring trend with baseline comparison and low hiring intensity"
                values={{
                  recentAvg: recentAvg.toFixed(1),
                  earlierAvg: earlierAvg.toFixed(1),
                  change: Math.abs(trendReason.changePercent ?? 0).toFixed(1),
                  rate: (trendReason.recentHiringRate ?? 0).toFixed(1),
                }}
              />
            ) : trendReason.code === 'declining' ? (
              <FormattedMessage
                id="companyChart.trendReason.declining"
                defaultMessage="Why this trend: the latest 3-month average ({recentAvg}) is {change}% below the earlier baseline ({earlierAvg})."
                description="Explanation shown for declining hiring trend with baseline comparison"
                values={{
                  recentAvg: recentAvg.toFixed(1),
                  earlierAvg: earlierAvg.toFixed(1),
                  change: Math.abs(trendReason.changePercent ?? 0).toFixed(1),
                }}
              />
            ) : (
              <FormattedMessage
                id="companyChart.trendReason.stable"
                defaultMessage="Why this trend: the latest 3-month average ({recentAvg}) is close to the earlier baseline ({earlierAvg}), so the change stays within the stable range."
                description="Explanation shown for stable hiring trend when latest and baseline averages are similar"
                values={{
                  recentAvg: recentAvg.toFixed(1),
                  earlierAvg: earlierAvg.toFixed(1),
                }}
              />
            )}
          </p>
        )}
        {!hasEnoughDataForTrends && (
          <p className={`${isCompact ? 'text-[11px]' : 'text-sm'} text-jc-muted italic`}>
            <FormattedMessage
              id="companyChart.insufficientData"
              defaultMessage="Limited data available - trends require at least 3 months of data"
              description="Message shown when there is not enough data to calculate hiring trends (less than 3 months of data available)"
            />
          </p>
        )}
      </div>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-full mx-auto overflow-visible"
        style={{ display: 'block' }}
      >
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = padding + chartHeight - (percent / 100) * chartHeight;
          return (
            <line
              key={percent}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="var(--jc-border)"
              strokeWidth="0.5"
              opacity="0.55"
            />
          );
        })}

        {/* Bars */}
        {sortedMonths.map((month, index) => {
          const value = validMonthlyCounts[month] || 0;
          const barHeight = (value / maxValue) * chartHeight;
          // Lay out each bar inside an equal-width slot so the last month stays inside the plot (was clipping when using barWidth + 4 steps)
          const x = padding + index * slotW + (slotW - barWidth) / 2;
          const y = padding + chartHeight - barHeight;
          
          // Format month name (e.g., "2024-04" -> "Apr" or "Apr 2024")
          const formatMonthName = (monthStr: string): string => {
            try {
              const [year, monthNum] = monthStr.split('-');
              const monthIndex = parseInt(monthNum, 10) - 1;
              const date = new Date(parseInt(year, 10), monthIndex);
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return monthNames[monthIndex] || monthStr.slice(-2);
            } catch {
              return monthStr.slice(-2);
            }
          };
          
          const monthName = formatMonthName(month);
          const hasSpaceForNumber = barHeight > minBarHeightForTopLabel;
          
          // Debug log for each rendered bar
          
          return (
            <g key={month}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={primaryColor}
                rx="2"
                className={`cursor-pointer touch-manipulation outline-none ${JD_MORE_INFO_CHART_HIT}`}
                style={{ cursor: 'pointer' }}
                onClick={(e) => toggleHiringBar(month, value, e.currentTarget)}
                onMouseEnter={(e) => prefersFineHoverHiring && openHiringBar(month, value, e.currentTarget)}
                onMouseLeave={() => prefersFineHoverHiring && scheduleHiringClose()}
              />
              {hasSpaceForNumber && value > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={y - (isCompact ? 2 : 3)}
                  textAnchor="middle"
                  fill="var(--jc-ink-secondary)"
                  fontSize={barTopFont}
                  fontWeight={600}
                  className="pointer-events-none"
                >
                  {value}
                </text>
              )}
              <text
                x={x + barWidth / 2}
                y={height - padding + (isCompact ? 11 : 14)}
                textAnchor="middle"
                fill="var(--jc-muted)"
                fontSize={axisFont}
                className="pointer-events-none"
              >
                {monthName}
              </text>
            </g>
          );
        })}

        {hiringTrendLine}
      </svg>

      {hiringPopover ? (
        <ChartPopoverPortal
          open
          anchorEl={hiringPopover.anchor}
          onClose={closeHiringPopover}
          showBackdrop={!prefersFineHoverHiring}
          width={isCompact ? 240 : 280}
          title={formatMonthPeriod(hiringPopover.monthKey)}
          onPopoverMouseEnter={prefersFineHoverHiring ? clearHiringHover : undefined}
          onPopoverMouseLeave={prefersFineHoverHiring ? scheduleHiringClose : undefined}
        >
          <div className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.monthKey" defaultMessage="Period" description="Monthly chart popover" />
              </span>
              <span className="font-semibold text-jc-ink text-right">{hiringPopover.monthKey}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.count" defaultMessage="Job postings" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{hiringPopover.value}</span>
            </div>
            {safeCompanyName ? (
              <p className="text-xs text-jc-muted pt-1 border-t border-jc-border/80">
                <FormattedMessage
                  id="jobChart.popover.companyContext"
                  defaultMessage="Based on {companyName} listings in our data."
                  description="Footnote for company chart popover"
                  values={{ companyName: safeCompanyName }}
                />
              </p>
            ) : null}
          </div>
        </ChartPopoverPortal>
      ) : null}
    </div>
  );
});

MonthlyHiringChart.displayName = 'MonthlyHiringChart';

interface StarburstChartProps {
  data: Record<string, number>;
  title: string;
  titleKey?: 'roleDistribution' | 'fieldDistribution' | 'roleLevelDistribution' | 'locationDistribution' | 'remotenessDistribution' | 'languageRequirements';
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
  maxItems?: number;
  density?: 'default' | 'compact';
}

/**
 * Starburst chart - radial bar visualization
 * Creates a star-like radial visualization with bars extending from the center
 */
export const StarburstChart: React.FC<StarburstChartProps> = memo(({
  data,
  title,
  titleKey,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
  maxItems = 8,
  density = 'default',
}) => {
  const intl = useIntl();
  const isCompact = density === 'compact';
  const safeCompanyName = toSafeIntlStringValue(companyName, locale);
  const [starburstPopover, setStarburstPopover] = useState<{ index: number; anchor: Element } | null>(null);
  const prefersFineHoverStarburst = usePrefersFineHover();
  const starburstHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStarburstHover = useCallback(() => {
    if (starburstHoverTimer.current !== null) {
      clearTimeout(starburstHoverTimer.current);
      starburstHoverTimer.current = null;
    }
  }, []);

  const scheduleStarburstClose = useCallback(() => {
    clearStarburstHover();
    starburstHoverTimer.current = setTimeout(() => {
      setStarburstPopover(null);
      starburstHoverTimer.current = null;
    }, 220);
  }, [clearStarburstHover]);

  useEffect(() => () => clearStarburstHover(), [clearStarburstHover]);

  const openStarburstBar = useCallback(
    (index: number, anchor: Element) => {
      clearStarburstHover();
      setStarburstPopover({ index, anchor });
    },
    [clearStarburstHover],
  );

  const closeStarburstPopover = useCallback(() => {
    clearStarburstHover();
    setStarburstPopover(null);
  }, [clearStarburstHover]);

  const toggleStarburstBar = useCallback((index: number, anchor: Element) => {
    setStarburstPopover((prev) => (prev?.index === index ? null : { index, anchor }));
  }, []);

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  // Filter out invalid values and create clean entries
  const validEntries = Object.entries(data)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;

  if (validEntries.length === 0) {
    return null;
  }

  // Sort by value descending and take top items
  const sortedEntries = validEntries
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxItems);

  const maxValue = Math.max(...sortedEntries.map(([, value]) => value));
  const totalValue = sortedEntries.reduce((sum, [, value]) => sum + value, 0);

  const innerSize = isCompact ? 158 : 220;
  /** Compact needs extra margin so radial labels (e.g. long location names) are not clipped on employer profile cards */
  const chartPadding = isCompact ? 40 : 48;
  const width = innerSize + chartPadding * 2;
  const height = innerSize + chartPadding * 2;
  const centerX = width / 2;
  const centerY = height / 2;
  const centerCircleRadius = isCompact ? 14 : 22;
  const maxRadius = innerSize / 2 - (isCompact ? 8 : 14);
  const barWidth = isCompact ? 9 : 14;
  const labelFontMain = isCompact ? 10 : 13;
  const labelFontPct = isCompact ? 9 : 11;
  const centerCountFont = isCompact ? 11 : 13;
  const startAngle = -Math.PI / 2;
  const angleStep = (2 * Math.PI) / sortedEntries.length;

  const primaryColor =
    brandColour && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(brandColour.trim())
      ? brandColour.trim()
      : jobChartVar.cta;
  const colors = [primaryColor, ...jobChartSeriesVars.slice(1)];

  // Create bars
  const bars = sortedEntries.map(([label, value], index) => {
    const angle = startAngle + (index * angleStep);
    const normalizedValue = value / maxValue;
    // Bars extend from center to outer radius, but center circle will overlap them
    const barLength = centerCircleRadius + (normalizedValue * (maxRadius - centerCircleRadius));
    
    // Calculate perpendicular points for bar width
    const perpAngle = angle + Math.PI / 2;
    const halfWidth = barWidth / 2;
    const perpX = Math.cos(perpAngle) * halfWidth;
    const perpY = Math.sin(perpAngle) * halfWidth;
    
    // Calculate bar endpoints
    const startX = centerX + Math.cos(angle) * centerCircleRadius;
    const startY = centerY + Math.sin(angle) * centerCircleRadius;
    const endX = centerX + Math.cos(angle) * barLength;
    const endY = centerY + Math.sin(angle) * barLength;
    
    // Create rectangle points
    const points = [
      `${startX + perpX},${startY + perpY}`,
      `${startX - perpX},${startY - perpY}`,
      `${endX - perpX},${endY - perpY}`,
      `${endX + perpX},${endY + perpY}`,
    ].join(' ');
    
    // Label position at the tip of the bar (endX, endY)
    // Add a small offset outward from the tip for better readability
    const labelOffset = isCompact ? 3 : 4;
    const labelX = endX + Math.cos(angle) * labelOffset;
    const labelY = endY + Math.sin(angle) * labelOffset;
    
    // Determine text anchor based on angle for better readability
    // Right side (0 to PI/2 and -PI/2 to 0): anchor to start (left)
    // Left side (PI/2 to 3*PI/2): anchor to end (right)
    // Top/Bottom: anchor to middle
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (angle > -Math.PI / 2 && angle < Math.PI / 2) {
      // Right side
      textAnchor = 'start';
    } else {
      // Left side
      textAnchor = 'end';
    }
    
    const percentage = ((value / totalValue) * 100).toFixed(1);
    
    return {
      label,
      value,
      percentage,
      points,
      angle,
      labelX,
      labelY,
      textAnchor,
      color: colors[index % colors.length],
    };
  });

  // Generate FAQ-style caption
  const generateCaption = () => {
    if (!safeCompanyName || sortedEntries.length === 0) return null;
    
    const topRoles = sortedEntries.slice(0, 3).map(([label]) => label);
    
    // Use titleKey for proper localization (title is already localized)
    if (titleKey === 'roleDistribution') {
      if (topRoles.length === 1) {
        return (
          <FormattedMessage
            id="companyChart.caption.roleDistributionSingle"
            defaultMessage="{companyName} hires mostly {role} roles."
            description="Caption text describing the most common job role type a company hires for, shown in role distribution chart"
            values={{ companyName: safeCompanyName, role: topRoles[0] }}
          />
        );
      } else if (topRoles.length === 2) {
        return (
          <FormattedMessage
            id="companyChart.caption.roleDistributionDouble"
            defaultMessage="{companyName} hires mostly {role1} and {role2} roles."
            description="Caption text describing the two most common job role types a company hires for, shown in role distribution chart"
            values={{ companyName: safeCompanyName, role1: topRoles[0], role2: topRoles[1] }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.roleDistributionTriple"
            defaultMessage="{companyName} hires mostly {role1}, {role2}, and {role3} roles."
            description="Caption text describing the three most common job role types a company hires for, shown in role distribution chart"
            values={{ companyName: safeCompanyName, role1: topRoles[0], role2: topRoles[1], role3: topRoles[2] }}
          />
        );
      }
    } else if (titleKey === 'fieldDistribution') {
      if (topRoles.length === 1) {
        return (
          <FormattedMessage
            id="companyChart.caption.fieldDistributionSingle"
            defaultMessage="{companyName} focuses mostly on {field}."
            description="Caption text describing the primary business field/domain a company operates in, shown in field distribution chart"
            values={{ companyName: safeCompanyName, field: topRoles[0] }}
          />
        );
      } else if (topRoles.length === 2) {
        return (
          <FormattedMessage
            id="companyChart.caption.fieldDistributionDouble"
            defaultMessage="{companyName} focuses mostly on {field1} and {field2}."
            description="Caption text describing the two primary business fields/domains a company operates in, shown in field distribution chart"
            values={{ companyName: safeCompanyName, field1: topRoles[0], field2: topRoles[1] }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.fieldDistributionTriple"
            defaultMessage="{companyName} focuses mostly on {field1}, {field2}, and {field3}."
            description="Caption text describing the three primary business fields/domains a company operates in, shown in field distribution chart"
            values={{ companyName: safeCompanyName, field1: topRoles[0], field2: topRoles[1], field3: topRoles[2] }}
          />
        );
      }
    } else if (titleKey === 'roleLevelDistribution') {
      if (topRoles.length === 1) {
        return (
          <FormattedMessage
            id="companyChart.caption.levelDistributionSingle"
            defaultMessage="{companyName} hires mostly at the {level} level."
            description="Caption text describing the most common experience level (junior, mid, senior) a company hires at, shown in level distribution chart"
            values={{ companyName: safeCompanyName, level: topRoles[0] }}
          />
        );
      } else if (topRoles.length === 2) {
        return (
          <FormattedMessage
            id="companyChart.caption.levelDistributionDouble"
            defaultMessage="{companyName} hires mostly at the {level1} and {level2} levels."
            description="Caption text describing the two most common experience levels a company hires at, shown in level distribution chart"
            values={{ companyName: safeCompanyName, level1: topRoles[0], level2: topRoles[1] }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.levelDistributionTriple"
            defaultMessage="{companyName} hires mostly at the {level1}, {level2}, and {level3} levels."
            description="Caption text describing the three most common experience levels a company hires at, shown in level distribution chart"
            values={{ companyName: safeCompanyName, level1: topRoles[0], level2: topRoles[1], level3: topRoles[2] }}
          />
        );
      }
    } else if (titleKey === 'locationDistribution') {
      if (topRoles.length === 1) {
        return (
          <FormattedMessage
            id="companyChart.caption.locationDistributionSingle"
            defaultMessage="{companyName} hires mostly in {location}."
            description="Caption text describing the primary location where a company posts jobs, shown in location distribution chart"
            values={{ companyName: safeCompanyName, location: topRoles[0] }}
          />
        );
      } else if (topRoles.length === 2) {
        return (
          <FormattedMessage
            id="companyChart.caption.locationDistributionDouble"
            defaultMessage="{companyName} hires mostly in {location1} and {location2}."
            description="Caption text describing the two primary locations where a company posts jobs, shown in location distribution chart"
            values={{ companyName: safeCompanyName, location1: topRoles[0], location2: topRoles[1] }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.locationDistributionTriple"
            defaultMessage="{companyName} hires mostly in {location1}, {location2}, and {location3}."
            description="Caption text describing the three primary locations where a company posts jobs, shown in location distribution chart"
            values={{ companyName: safeCompanyName, location1: topRoles[0], location2: topRoles[1], location3: topRoles[2] }}
          />
        );
      }
    } else if (titleKey === 'remotenessDistribution') {
      // Remoteness has specific keys: remote, hybrid, onsite
      const remoteCount = sortedEntries.find(([label]) => label.toLowerCase() === 'remote')?.[1] || 0;
      const hybridCount = sortedEntries.find(([label]) => label.toLowerCase() === 'hybrid')?.[1] || 0;
      const onsiteCount = sortedEntries.find(([label]) => label.toLowerCase() === 'onsite')?.[1] || 0;
      
      const maxType = remoteCount > hybridCount && remoteCount > onsiteCount ? 'remote'
        : hybridCount > onsiteCount ? 'hybrid'
        : 'onsite';
      
      if (maxType === 'remote') {
        return (
          <FormattedMessage
            id="companyChart.caption.remotenessRemote"
            defaultMessage="{companyName} hires mostly remote positions."
            description="Caption text describing that a company primarily offers remote work positions, shown in remoteness distribution chart"
            values={{ companyName: safeCompanyName }}
          />
        );
      } else if (maxType === 'hybrid') {
        return (
          <FormattedMessage
            id="companyChart.caption.remotenessHybrid"
            defaultMessage="{companyName} hires mostly hybrid positions."
            description="Caption text describing that a company primarily offers hybrid work positions (mix of remote and onsite), shown in remoteness distribution chart"
            values={{ companyName: safeCompanyName }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.remotenessOnsite"
            defaultMessage="{companyName} hires mostly onsite positions."
            description="Caption text describing that a company primarily offers onsite work positions, shown in remoteness distribution chart"
            values={{ companyName: safeCompanyName }}
          />
        );
      }
    } else if (titleKey === 'languageRequirements') {
      if (topRoles.length === 1) {
        return (
          <FormattedMessage
            id="companyChart.caption.languageRequirementsSingle"
            defaultMessage="{companyName} typically requires {language}."
            description="Caption text describing the primary language requirement for jobs at a company, shown in language requirements chart"
            values={{ companyName: safeCompanyName, language: topRoles[0] }}
          />
        );
      } else if (topRoles.length === 2) {
        return (
          <FormattedMessage
            id="companyChart.caption.languageRequirementsDouble"
            defaultMessage="{companyName} typically requires {language1} and {language2}."
            description="Caption text describing the two primary language requirements for jobs at a company, shown in language requirements chart"
            values={{ companyName: safeCompanyName, language1: topRoles[0], language2: topRoles[1] }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="companyChart.caption.languageRequirementsTriple"
            defaultMessage="{companyName} typically requires {language1}, {language2}, and {language3}."
            description="Caption text describing the three primary language requirements for jobs at a company, shown in language requirements chart"
            values={{ companyName: safeCompanyName, language1: topRoles[0], language2: topRoles[1], language3: topRoles[2] }}
          />
        );
      }
    }
    
    return null;
  };

  const caption = generateCaption();

  const starburstActiveBar = starburstPopover ? bars[starburstPopover.index] : null;

  return (
    <div className={`starburst-chart ${className} flex flex-col items-center`}>
      <p className={`${isCompact ? 'text-[11px] mb-0.5' : 'text-xs mb-1'} text-jc-muted w-full`}>
        <FormattedMessage
          id="jobChart.segmentHint"
          defaultMessage="Tap or hover a segment for details."
          description="Hint for interactive donut and pill chart"
        />
      </p>
      <h3 className={`${isCompact ? 'text-xs mb-1' : 'text-sm mb-1.5'} font-semibold text-jc-ink w-full`}>{title}</h3>
      {caption && (
        <p className={`${isCompact ? 'text-xs mb-1' : 'text-sm mb-2'} text-jc-muted leading-snug w-full`}>
          {caption}
        </p>
      )}
      <div
        className={`flex flex-col md:flex-row ${isCompact ? 'gap-1.5' : 'gap-2'} items-center justify-center w-full overflow-visible`}
      >
        <svg
          width={innerSize}
          height={innerSize}
          className={`w-full mx-auto overflow-visible ${isCompact ? 'max-w-[168px]' : 'max-w-[220px]'}`}
          viewBox={`0 0 ${width} ${height}`}
        >
          {[0.25, 0.5, 0.75, 1].map((ratio) => {
            const r = centerCircleRadius + (ratio * (maxRadius - centerCircleRadius));
            return (
              <circle
                key={ratio}
                cx={centerX}
                cy={centerY}
                r={r}
                fill="none"
                stroke="var(--jc-border)"
                strokeWidth="0.5"
                opacity="0.45"
              />
            );
          })}

          {sortedEntries.map((_, index) => {
            const angle = startAngle + (index * angleStep);
            const x2 = centerX + Math.cos(angle) * maxRadius;
            const y2 = centerY + Math.sin(angle) * maxRadius;
            return (
              <line
                key={index}
                x1={centerX}
                y1={centerY}
                x2={x2}
                y2={y2}
                stroke="var(--jc-border)"
                strokeWidth="0.5"
                opacity="0.45"
              />
            );
          })}

          {/* Bars */}
          {bars.map((bar, index) => (
            <g key={index}>
              <polygon
                points={bar.points}
                fill={bar.color}
                opacity="0.88"
                className={`cursor-pointer touch-manipulation outline-none ${JD_MORE_INFO_CHART_HIT}`}
                style={{ cursor: 'pointer' }}
                onClick={(e) => toggleStarburstBar(index, e.currentTarget)}
                onMouseEnter={(e) => prefersFineHoverStarburst && openStarburstBar(index, e.currentTarget)}
                onMouseLeave={() => prefersFineHoverStarburst && scheduleStarburstClose()}
              />
              <text
                x={bar.labelX}
                y={bar.labelY - (isCompact ? 2 : 3)}
                textAnchor={bar.textAnchor}
                dominantBaseline="middle"
                fill="var(--jc-ink-secondary)"
                fontSize={labelFontMain}
                fontWeight={600}
                className="pointer-events-none"
              >
                {bar.label}
              </text>
              <text
                x={bar.labelX}
                y={bar.labelY + (isCompact ? 6 : 7)}
                textAnchor={bar.textAnchor}
                dominantBaseline="middle"
                fill="var(--jc-muted)"
                fontSize={labelFontPct}
                className="pointer-events-none"
              >
                {bar.percentage}%
              </text>
            </g>
          ))}

          <circle
            cx={centerX}
            cy={centerY}
            r={centerCircleRadius}
            fill="var(--jc-surface)"
            stroke="var(--jc-border)"
            strokeWidth="1"
          />
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--jc-ink-secondary)"
            fontSize={centerCountFont}
            fontWeight={700}
            className="pointer-events-none"
          >
            {sortedEntries.length}
          </text>
        </svg>
      </div>

      {starburstPopover && starburstActiveBar ? (
        <ChartPopoverPortal
          open
          anchorEl={starburstPopover.anchor}
          onClose={closeStarburstPopover}
          showBackdrop={!prefersFineHoverStarburst}
          width={isCompact ? 240 : 280}
          title={starburstActiveBar.label}
          onPopoverMouseEnter={prefersFineHoverStarburst ? clearStarburstHover : undefined}
          onPopoverMouseLeave={prefersFineHoverStarburst ? scheduleStarburstClose : undefined}
        >
          <div className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.count" defaultMessage="Job postings" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{starburstActiveBar.value}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.share" defaultMessage="Share of chart" description="Label in chart popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{starburstActiveBar.percentage}%</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-[#5A4A3A] dark:text-[#B8A48C]">
                <FormattedMessage id="jobChart.popover.categoriesShown" defaultMessage="Categories shown" description="Starburst popover" />
              </span>
              <span className="font-semibold text-jc-ink tabular-nums">{sortedEntries.length}</span>
            </div>
            <p className="text-xs text-jc-muted pt-1 border-t border-jc-border/80">{title}</p>
            {safeCompanyName ? (
              <p className="text-xs text-jc-muted">
                <FormattedMessage
                  id="jobChart.popover.companyContext"
                  defaultMessage="Based on {companyName} listings in our data."
                  description="Footnote for company chart popover"
                  values={{ companyName: safeCompanyName }}
                />
              </p>
            ) : null}
          </div>
        </ChartPopoverPortal>
      ) : null}
    </div>
  );
});

StarburstChart.displayName = 'StarburstChart';
